import type { Doc } from "@convex/_generated/dataModel";
import type { SubscriptionRenewalGrant } from "@convex/validators";
import type { WithoutSystemFields } from "convex/server";
import type Stripe from "stripe";
import { z } from "zod";
import { DEFAULT_CREDITS_EXPIRATION_DAYS } from "./credits";

/**
 * Pure Stripe→Convex-document mappers, shared by the Next.js Stripe webhook
 * (`src/`) and `convex/init.ts`'s preview-deployment seeding. No `ctx`, no
 * I/O — every function here takes a Stripe object (or its metadata) and
 * returns plain data.
 */

/**
 * Price metadata schema for Stripe product pricing.
 */
export const PriceMetadata = z.object({
  /** Unified credits included per billing period */
  credits_unified_included: z.string().optional(),
  /** Discount rate for extra credit purchases (percentage, e.g., "20" for 20% off) */
  credits_discount_rate: z.string().optional(),
  /** Number of days subscription credits are valid from allocation date */
  credits_expiration_days: z.string().optional(),
  /** Number of concurrent exports allowed */
  concurrent_exports: z.string().optional(),
});

export type SubscriptionTier = "standard" | "premium";

/**
 * Converts a Stripe unix-seconds timestamp to the milliseconds every Convex
 * timestamp field expects. Stripe sends seconds; Convex stores milliseconds;
 * mixing the two up doesn't throw or fail typechecking — it silently writes
 * a 1970-era date. That's the entire reason this is a named function instead
 * of `* 1000` inlined at each call site.
 */
export function stripeSecondsToMs(secs: number): number {
  return secs * 1000;
}

/**
 * `Stripe.Subscription` -> `upsertSubscription` mutation args (minus
 * `serviceSecret`, which the caller owns). Handles all 8 optional-timestamp
 * conversions (`cancelAt`, `canceledAt`, `currentPeriodStart`,
 * `currentPeriodEnd`, `created`, `endedAt`, `trialStart`, `trialEnd`) plus
 * tier, status, quantity, and the metadata merge.
 *
 * The 5 genuinely-optional timestamps (`cancelAt`, `canceledAt`, `endedAt`,
 * `trialStart`, `trialEnd`) map an absent Stripe value to `undefined`, never
 * `null`. `upsertSubscription` calls `ctx.db.replace()` on its update path,
 * which overwrites the whole document — an omitted key clears a
 * previously-set field, but a `null` would stick. This is what lets an
 * un-cancel round-trip correctly: Stripe sends `cancel_at: null`, and the
 * stored `cancelAt` must actually disappear, not become `null`.
 *
 * The billing period comes from `subscription.items.data[0]
 * .current_period_start/_end`, not the subscription root — this Stripe API
 * version has no root-level period fields.
 */
export function mapSubscriptionToUpsertArgs(
  subscription: Stripe.Subscription,
  userId: string,
  tier: SubscriptionTier,
  creditsIncluded: number | undefined,
): WithoutSystemFields<Doc<"subscriptions">> {
  const item = subscription.items.data[0];

  return {
    stripeId: subscription.id,
    userId,
    metadata: {
      ...(subscription.metadata ?? {}),
      ...(creditsIncluded !== undefined
        ? { credits_included: creditsIncluded }
        : {}),
    },
    status: subscription.status,
    priceId: item.price.id,
    tier,
    quantity: 1,
    cancelAtPeriodEnd: subscription.cancel_at_period_end,
    cancelAt: subscription.cancel_at
      ? stripeSecondsToMs(subscription.cancel_at)
      : undefined,
    canceledAt: subscription.canceled_at
      ? stripeSecondsToMs(subscription.canceled_at)
      : undefined,
    currentPeriodStart: stripeSecondsToMs(item.current_period_start),
    currentPeriodEnd: stripeSecondsToMs(item.current_period_end),
    created: stripeSecondsToMs(subscription.created),
    endedAt: subscription.ended_at
      ? stripeSecondsToMs(subscription.ended_at)
      : undefined,
    trialStart: subscription.trial_start
      ? stripeSecondsToMs(subscription.trial_start)
      : undefined,
    trialEnd: subscription.trial_end
      ? stripeSecondsToMs(subscription.trial_end)
      : undefined,
  };
}

/**
 * Tier for a subscription, from its price's `lookup_key` — the single source
 * for the stored tier and every analytics `plan_tier`. Any key that doesn't
 * mention "premium" (including a missing one) is `"standard"` — there is no
 * third tier and no error case. A substring match, so legacy keys
 * (`premium_year_legacy`) resolve too.
 */
export function derivePriceTier(
  lookupKey: string | null | undefined,
): SubscriptionTier {
  return lookupKey?.toLowerCase().includes("premium") ? "premium" : "standard";
}

/** Lifted from the `product.created/updated` case of the webhook route. */
export function mapProduct(
  product: Stripe.Product,
): WithoutSystemFields<Doc<"products">> {
  return {
    stripeId: product.id,
    active: product.active,
    name: product.name,
    description: product.description ?? undefined,
    image: product.images?.[0] ?? undefined,
    marketingFeatures: product.marketing_features
      ? product.marketing_features.map((f) => f.name ?? "")
      : [],
    metadata: product.metadata,
  };
}

/** Lifted from the `price.created/updated` case of the webhook route. */
export function mapPrice(
  price: Stripe.Price,
): WithoutSystemFields<Doc<"prices">> {
  if (!price.lookup_key) {
    throw new Error(
      `Price ${price.id} is missing lookup_key, which is required for upsert.`,
    );
  }
  return {
    stripeId: price.id,
    productId: typeof price.product === "string" ? price.product : "",
    active: price.active,
    currency: price.currency,
    type: price.type,
    unitAmount: price.unit_amount ?? undefined,
    interval: price.recurring?.interval,
    intervalCount: price.recurring?.interval_count,
    metadata: price.metadata ?? undefined,
    lookupKey: price.lookup_key,
  };
}

/** `credits_included` for a subscription's metadata, from its price's metadata. */
export function creditsIncludedFromPriceMetadata(
  metadata: Record<string, string> | null | undefined,
): number | undefined {
  if (!metadata) return undefined;
  const parsed = PriceMetadata.safeParse(metadata);
  if (!parsed.success || !parsed.data.credits_unified_included)
    return undefined;
  return Number(parsed.data.credits_unified_included);
}

export interface InvoiceCreditAllocation {
  totalCredits: number;
  expirationDays: number;
}

/**
 * The credit amount and expiration for an `invoice.paid` renewal, from the
 * price's metadata. `billing_reason` is observability only and plays no
 * part in this. This does not gate *whether* credits are granted — `route.ts`
 * decides that itself (no subscription on the invoice; a still-trialing
 * subscription) before this ever runs, because one of those checks has to
 * happen before the subscription is fetched and the other before
 * product/price are — neither can move into a single shared function
 * without adding I/O route.ts currently skips for those cases.
 */
export function deriveInvoiceCreditAllocation(
  priceMetadata: z.infer<typeof PriceMetadata>,
): InvoiceCreditAllocation {
  return {
    totalCredits: Number(priceMetadata.credits_unified_included ?? 0),
    expirationDays: priceMetadata.credits_expiration_days
      ? Number(priceMetadata.credits_expiration_days)
      : DEFAULT_CREDITS_EXPIRATION_DAYS,
  };
}

/**
 * The `subscription_renewal` grant the `invoice.paid` webhook made for a
 * subscription's current period, rebuilt from the subscription alone — for
 * seeding a deployment whose webhook never saw that invoice. `paidAt` is the
 * period start, so the seeded batch expires when the real one would have.
 * Throws on anything the webhook would not have granted from.
 */
export function renewalGrantForSubscription(
  userId: string,
  subscription: Stripe.Subscription,
): SubscriptionRenewalGrant {
  const item = subscription.items.data[0];
  const invoiceId =
    typeof subscription.latest_invoice === "string"
      ? subscription.latest_invoice
      : subscription.latest_invoice?.id;
  if (!invoiceId) {
    throw new Error(`Subscription ${subscription.id} has no latest invoice`);
  }
  const allocation = deriveInvoiceCreditAllocation(
    PriceMetadata.parse(item.price.metadata ?? {}),
  );
  if (allocation.totalCredits <= 0) {
    throw new Error(`Price ${item.price.id} includes no credits`);
  }
  return {
    kind: "subscription_renewal",
    userId,
    invoiceId,
    ...allocation,
    paidAt: stripeSecondsToMs(item.current_period_start),
  };
}
